/**
 * RouteBuilderStep - Optional step for defining custom routes
 * 
 * Allows venues to define custom routes to each destination area.
 * If no custom route is defined, the fallback logic is used.
 * 
 * This is SEPARATE from Journey Builder (movement step editing).
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, 
  Plus, 
  Trash2, 
  ChevronDown, 
  ChevronRight,
  ArrowRight,
  Save,
  Navigation,
  Check,
  Info
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const RouteBuilderStep = ({
  areas = [],
  customRoutes = [],
  onSaveRoutes,
  onSkip,
  loading = false
}) => {
  // Local state for editing routes
  const [routes, setRoutes] = useState([]);
  const [expandedDestination, setExpandedDestination] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Initialize routes from props
  useEffect(() => {
    if (customRoutes && customRoutes.length > 0) {
      setRoutes(customRoutes);
    }
  }, [customRoutes]);
  
  // Get all non-start areas as potential destinations
  const destinations = areas.filter((a, i) => i > 0);
  const startArea = areas[0];
  
  // Get route for a destination
  const getRouteForDestination = (destAreaId) => {
    return routes.find(r => r.destination_area_id === destAreaId);
  };
  
  // Check if destination has custom route
  const hasCustomRoute = (destAreaId) => {
    const route = getRouteForDestination(destAreaId);
    return route && route.route_steps && route.route_steps.length > 0;
  };
  
  // Toggle custom route for a destination
  const toggleCustomRoute = (destAreaId) => {
    if (hasCustomRoute(destAreaId)) {
      // Remove custom route
      setRoutes(routes.filter(r => r.destination_area_id !== destAreaId));
    } else {
      // Create default custom route (start -> destination)
      const destArea = areas.find(a => a.area_id === destAreaId);
      const newRoute = {
        destination_area_id: destAreaId,
        route_steps: [
          { type: 'area', area_id: startArea?.area_id || areas[0]?.area_id },
          { type: 'area', area_id: destAreaId }
        ]
      };
      setRoutes([...routes, newRoute]);
    }
  };
  
  // Add step to a route
  const addStepToRoute = (destAreaId, stepType, stepData = {}) => {
    setRoutes(routes.map(route => {
      if (route.destination_area_id !== destAreaId) return route;
      
      // Insert before the last step (destination)
      const steps = [...route.route_steps];
      const insertIndex = steps.length - 1;
      
      if (stepType === 'area') {
        steps.splice(insertIndex, 0, { type: 'area', area_id: stepData.area_id });
      } else if (stepType === 'custom') {
        steps.splice(insertIndex, 0, { type: 'custom', label: stepData.label || 'Walk through...' });
      }
      
      return { ...route, route_steps: steps };
    }));
  };
  
  // Remove step from route
  const removeStepFromRoute = (destAreaId, stepIndex) => {
    setRoutes(routes.map(route => {
      if (route.destination_area_id !== destAreaId) return route;
      
      // Don't allow removing first (start) or last (destination) step
      if (stepIndex === 0 || stepIndex === route.route_steps.length - 1) return route;
      
      const steps = [...route.route_steps];
      steps.splice(stepIndex, 1);
      return { ...route, route_steps: steps };
    }));
  };
  
  // Update step in route
  const updateStepInRoute = (destAreaId, stepIndex, updates) => {
    setRoutes(routes.map(route => {
      if (route.destination_area_id !== destAreaId) return route;
      
      const steps = [...route.route_steps];
      steps[stepIndex] = { ...steps[stepIndex], ...updates };
      return { ...route, route_steps: steps };
    }));
  };
  
  // Save routes
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveRoutes(routes);
      toast.success('Routes saved successfully');
    } catch (error) {
      toast.error('Failed to save routes');
    } finally {
      setSaving(false);
    }
  };
  
  // Get area name by ID
  const getAreaName = (areaId) => {
    const area = areas.find(a => a.area_id === areaId);
    return area?.area_name || area?.name || 'Unknown Area';
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }
  
  if (areas.length < 2) {
    return (
      <div className="text-center py-8">
        <Info className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Not enough areas</h3>
        <p className="text-gray-600 mb-4">You need at least 2 areas to define custom routes.</p>
        <Button onClick={onSkip}>Continue</Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <Navigation className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Define Custom Routes</h2>
        <p className="text-gray-600 max-w-lg mx-auto">
          Optionally define how visitors should navigate to each area. 
          If you skip this, a default route will be generated automatically.
        </p>
      </div>
      
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>How it works:</strong> Custom routes let you define the exact path visitors should take to reach each destination. 
          You can add intermediate stops and custom navigation notes.
        </div>
      </div>
      
      {/* Start Area Display */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-xs text-emerald-600 uppercase tracking-wider font-medium">Start Point</div>
            <div className="text-lg font-semibold text-gray-900">{startArea?.area_name || startArea?.name || 'First Area'}</div>
          </div>
        </div>
      </div>
      
      {/* Destination Routes */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Routes to Destinations</h3>
        
        {destinations.map((dest) => {
          const destId = dest.area_id;
          const hasRoute = hasCustomRoute(destId);
          const route = getRouteForDestination(destId);
          const isExpanded = expandedDestination === destId;
          
          return (
            <div 
              key={destId}
              className={`border rounded-lg overflow-hidden transition-all ${
                hasRoute ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-200'
              }`}
            >
              {/* Destination Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedDestination(isExpanded ? null : destId)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    hasRoute ? 'bg-emerald-500' : 'bg-gray-300'
                  }`}>
                    {hasRoute ? (
                      <Check className="w-4 h-4 text-white" />
                    ) : (
                      <ArrowRight className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{dest.area_name || dest.name}</div>
                    <div className="text-sm text-gray-500">
                      {hasRoute ? `Custom route (${route.route_steps.length} steps)` : 'Using default route'}
                    </div>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </div>
              
              {/* Expanded Route Editor */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden border-t border-gray-200"
                  >
                    <div className="p-4 space-y-4 bg-white">
                      {/* Toggle Custom Route */}
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`route-type-${destId}`}
                            checked={!hasRoute}
                            onChange={() => hasRoute && toggleCustomRoute(destId)}
                            className="w-4 h-4 text-emerald-600"
                          />
                          <span className="text-sm text-gray-700">Use default route</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`route-type-${destId}`}
                            checked={hasRoute}
                            onChange={() => !hasRoute && toggleCustomRoute(destId)}
                            className="w-4 h-4 text-emerald-600"
                          />
                          <span className="text-sm text-gray-700">Define custom route</span>
                        </label>
                      </div>
                      
                      {/* Custom Route Steps */}
                      {hasRoute && route && (
                        <div className="space-y-2 pl-4 border-l-2 border-emerald-300">
                          {route.route_steps.map((step, idx) => {
                            const isFirst = idx === 0;
                            const isLast = idx === route.route_steps.length - 1;
                            
                            return (
                              <div key={idx} className="flex items-center gap-2">
                                {/* Step Number */}
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  isFirst ? 'bg-emerald-500 text-white' : 
                                  isLast ? 'bg-blue-500 text-white' : 
                                  'bg-gray-200 text-gray-600'
                                }`}>
                                  {idx + 1}
                                </div>
                                
                                {/* Step Content */}
                                {step.type === 'area' ? (
                                  <select
                                    value={step.area_id}
                                    onChange={(e) => updateStepInRoute(destId, idx, { area_id: e.target.value })}
                                    disabled={isFirst || isLast}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
                                  >
                                    {areas.map(a => (
                                      <option key={a.area_id} value={a.area_id}>
                                        {a.area_name || a.name}
                                        {a.area_id === startArea?.area_id ? ' (Start)' : ''}
                                        {a.area_id === destId ? ' (Destination)' : ''}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    value={step.label || ''}
                                    onChange={(e) => updateStepInRoute(destId, idx, { label: e.target.value })}
                                    placeholder="e.g., Walk through main corridor"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                  />
                                )}
                                
                                {/* Remove Button (not for first/last) */}
                                {!isFirst && !isLast && (
                                  <button
                                    onClick={() => removeStepFromRoute(destId, idx)}
                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          
                          {/* Add Step Buttons */}
                          <div className="flex gap-2 pt-2">
                            <button
                              onClick={() => addStepToRoute(destId, 'area', { area_id: areas[1]?.area_id })}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm text-emerald-600 hover:bg-emerald-50 rounded-lg"
                            >
                              <Plus className="w-4 h-4" />
                              Add Area Stop
                            </button>
                            <button
                              onClick={() => addStepToRoute(destId, 'custom', { label: '' })}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                            >
                              <Plus className="w-4 h-4" />
                              Add Note
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
      
      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <button
          onClick={onSkip}
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          Skip for now
        </button>
        <div className="flex gap-3">
          {routes.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setRoutes([])}
            >
              Clear All
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save & Continue
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RouteBuilderStep;
